import {
  useEffect,
  RefObject,
} from "react";
import { updateScrollToNow } from "./utils/calculateScrollToNow";

interface UseAutoScrollToNowProps {
  containerRef: RefObject<HTMLDivElement>;
  enabled: boolean;
  isFetched: boolean;
  date: Date;
  startHour: number;
  endHour: number;
  fixedWidth: number;
  dataKey?: string | number; // Chave para detectar mudanças nos dados (quando API retorna)
  isProgrammaticScrollRef?: { current: boolean }; // Ref para marcar scroll programático
}

/**
 * Hook para fazer scroll automático mantendo o "now" sempre no meio da tela
 * Executa continuamente quando toggle está ativo (a cada 1 segundo)
 * Também executa quando a API retorna dados (dataKey muda)
 * Se já chegou ao fim do range (ex: 2h do dia seguinte), não faz scroll
 */
export function useAutoScrollToNow({
  containerRef,
  enabled,
  isFetched,
  date,
  startHour,
  endHour,
  fixedWidth,
  dataKey,
  isProgrammaticScrollRef,
}: UseAutoScrollToNowProps): void {
  useEffect(() => {
    if (!enabled || !isFetched || !containerRef.current) {
      return undefined;
    }

    const updateScroll = () => {
      if (!containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const visibleWidth = container.clientWidth;
      const maxScrollLeft = fixedWidth - visibleWidth;

      updateScrollToNow(
        container,
        date,
        startHour,
        endHour,
        fixedWidth,
        maxScrollLeft,
        isProgrammaticScrollRef
      );
    };

    // Aguarda renderização do DOM e executa imediatamente
    const timeoutId = setTimeout(updateScroll, 100);

    // Intervalo para atualizar continuamente (a cada 1 segundo)
    const intervalId = setInterval(updateScroll, 1000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [enabled, isFetched, date, startHour, endHour, fixedWidth, containerRef, dataKey]);
}
